import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import { AddCircle, Construction, ImportExport, PriceChange } from "@mui/icons-material"
import RecordModal from "../../components/RecordModal/RecordModal"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { API } from "../../lib/api"
import { FormProvider } from "../../contexts/CreateRecordFormContext"
import { useGridApiRef } from "@mui/x-data-grid-pro"
import Feedback from "../../components/Feedback/Feedback"
import CollapsibleGridCard from "../../components/CollapsibleGridCard"
import SingleCatalogColumns from "../../lib/TableColumns/SingleCatalogColumns"
import { GRID_SESSION_KEYS } from "../../lib/gridSessionState"
import { getForecastMarginProductGroups, useAppSettings } from "../../contexts/AppSettingsContext"

const SingleCatalog = () => {
    // -- Page State -- //
    const [catalogData, setCatalogData] = useState({})
    const [loading, setLoading] = useState(true)
    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [feedback, setFeedback] = useState({})
    const [selectedRows, setSelectedRows] = useState([])
    const [openUpdateModal, setOpenUpdateModal] = useState(false)
    const [openPriceIncreaseModal, setPriceIncreaseModal] = useState(false)
    const [priceIncreaseValue, setPriceIncreaseValue] = useState(0)

    const { cid } = useParams()
    const { applicationSettings } = useAppSettings()
    const apiRef = useGridApiRef()
    const columns = SingleCatalogColumns()
    const productCodeOptions = getForecastMarginProductGroups(applicationSettings)
    const activeStatusOptions = [
        { label: "Active", value: true },
        { label: "Inactive", value: false },
    ]
    const createItemFields = [
        { name: "manufacturerPartNumber", label: "Manufacturer Part #", type: "text" },
        { name: "category", label: "Category", type: "text" },
        { name: "productCode", label: "Product Code", type: "autocomplete", options: productCodeOptions },
        { name: "cost", label: "Unit Cost", type: "number" },
        { name: "price", label: "Unit Price", type: "number" },
        { name: "description", label: "Description", type: "text", multiline: true, rows: 6 },
        { name: "active", label: "Active", type: "switch" },
    ]
    const updateItemFields = [
        { name: "manufacturerPartNumber", label: "Manufacturer Part #", type: "text" },
        { name: "category", label: "Category", type: "text" },
        { name: "productCode", label: "Product Code", type: "autocomplete", options: productCodeOptions },
        { name: "cost", label: "Unit Cost", type: "number" },
        { name: "price", label: "Unit Price", type: "number" },
        { name: "active", label: "Active", type: "autocomplete", options: activeStatusOptions },
    ]

    // -- Modal Handlers -- //
    const handleOpenCreateModal = () => setOpenCreateModal(true)
    const handleCloseCreateModal = () => setOpenCreateModal(false)

    const handleOpenUpdateModal = () => selectedRows.length ? setOpenUpdateModal(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleCloseUpdateModal = () => setOpenUpdateModal(false)

    const handlePriceChangeModal = () => selectedRows.length ? setPriceIncreaseModal(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleClosePriceChangeModal = () => {
        setPriceIncreaseModal(false)
        setPriceIncreaseValue(0)
    }

    // -- Grid Selection -- //
    const handleSelectionChange = (selection) => setSelectedRows(selection)

    // -- Local State Helpers -- //
    const addProduct = (newProduct) =>
        setCatalogData(prev => ({
            ...prev,
            products: [...(prev.products || []), newProduct]
        }))

    // -- Create Product -- //
    const handleSubmit = async (data) => {
        const serverReadyData = {
            ...data,
            lastModifiedBy: localStorage?.id
        }

        const response = await API.Products.Create(cid, serverReadyData)

        if (response.status === 200) {
            addProduct(response.data.data)
            setFeedback({open: true, message: response.data.message, type: "success"})
        } else {
            setFeedback({open: true, message: response?.data?.message || "Error Creating Product...", type: "error"})
        }

        handleCloseCreateModal()
    }

    // -- Update Products -- //
    const handleUpdateSubmit = async (data) => {
        let updatedFields = { lastModifiedBy: localStorage.name }

        Object.keys(data).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(data, key) && data[key] !== "" && data[key] !== null && typeof data[key] !== "undefined") {
                const nextValue =
                    key === "active" && typeof data[key] === "object" && data[key] !== null
                        ? data[key].value
                        : data[key]

                Object.assign(updatedFields, { [key]: nextValue } )
            }
        })

        const updatePayload = selectedRows.map((selectedRow) => ({
            _id: selectedRow,
            data: updatedFields
        }))

        const response = await API.Products.UpdateMulti(updatePayload)

        if (response.status === 200) {
            setCatalogData(prevCatalog => ({
                ...prevCatalog,
                products: prevCatalog.products.map(product => {
                    const updated = response.data.data.find(update => update._id === product._id);
                    return updated ? updated : product;
                })
            }));

            setFeedback(prevData => ({...prevData, open: true, message: response.data.message, type: 'success' }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response?.data?.message || "Error updating item(s)", type: 'error' }))
        }

        handleCloseUpdateModal(false)
    }

    // -- Deactivate Products -- //
    const handleDeleteSubmit = async () => {
        const response = await API.Products.Delete(selectedRows)

        if (response.status === 200) {
            setCatalogData(prevCatalog => ({
                ...prevCatalog,
                products: prevCatalog.products.map(product =>
                    selectedRows.includes(product._id)
                        ? { ...product, active: false }
                        : product
                )
            }));

            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'success' }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message || "Failed to mark product(s) inactive.", type: 'error' }))
        }

        setSelectedRows([])
        handleCloseUpdateModal()
    }

    // -- Export Catalog -- //
    const handleExportData = async () => {
        if (!catalogData.products?.length) {
            setFeedback(prevData => ({
                ...prevData,
                open: true,
                message: "Cannot Export with a length of 0!",
                type: "error",
            }))

            return
        }

        const today = new Date()
        const formattedDate = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`

        apiRef.current.exportDataAsCsv({
            fileName: `Catalog - ${formattedDate}`
        })
    }

    // -- Apply Price Increase -- //
    const handlePriceIncrease = async () => {
        const response = await API.Products.PriceIncrease(selectedRows,priceIncreaseValue)

        if (response.status === 200) {
            setFeedback({open: true, message: response.data.message, type: "success"})
        } else {
            setFeedback({open: true, message: "Failed to update products...", type: "error"})
        }
        handleClosePriceChangeModal()
    }

    // -- Collapsible Grid Card Actions -- //
    const actionButtons = [
        { title: "Create New Item", onClick: handleOpenCreateModal, icon: AddCircle, color: "primary" },
        { title: "Update Selected Record(s)", onClick: handleOpenUpdateModal, icon: Construction, color: "primary" },
        { title: "Export Catalog to Excel", onClick: handleExportData, icon: ImportExport, color: "primary" },
        { title: "Apply Price Increase", onClick: handlePriceChangeModal, icon: PriceChange, color: "primary" },
    ]

    // -- Fetch Catalog -- //
    useEffect(() => {
        async function fetchData() {
            const response = await API.Catalogs.FetchOne(cid)
            setCatalogData(response)
            setLoading(false)
        }

        fetchData()
    },[cid])

    return (
        <BaseWrapper
            content={
                loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                <FormProvider>
                    <Feedback props={feedback} setFeedback={setFeedback} />
                    <RecordModal open={openCreateModal} title={"Create New Item"} handleClose={handleCloseCreateModal} fields={createItemFields} onSubmit={handleSubmit} initialValues={{ active: true }} />
                    <RecordModal open={openUpdateModal} handleClose={handleCloseUpdateModal} fields={updateItemFields} title="Update Item(s)" onSubmit={handleUpdateSubmit} onDelete={handleDeleteSubmit} isUpdate={true} />

                    {/* -- Catalog Products Grid -- */}
                    <div className="col-xl-12 col-md-12" style={{marginTop: "1em", marginBottom: "1em"}}>
                        <CollapsibleGridCard
                            title="Catalogs"
                            linkLabel={catalogData?.name || "Catalog"}
                            rows={catalogData.products || []}
                            columns={columns}
                            getRowId={(row) => row._id}
                            checkboxSelection
                            rowSelectionModel={selectedRows}
                            onRowSelectionModelChange={handleSelectionChange}
                            actionButtons={actionButtons}
                            apiRef={apiRef}
                            sessionStorageKey={`${GRID_SESSION_KEYS.engineeringSingleCatalog}-${cid}`}
                            sessionUserId={localStorage.getItem("id")}
                            initialPageSize={50}
                            pageSizeOptions={[50, 100, 500]}
                        />
                    </div>

                    <Dialog open={openPriceIncreaseModal} onClose={handleClosePriceChangeModal} fullWidth maxWidth="sm">
                        <DialogTitle>Add Discount</DialogTitle>

                        <DialogContent sx={{ paddingTop: 1 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label={"Percent"}
                                value={priceIncreaseValue}
                                onChange={(e) => setPriceIncreaseValue(e.target.value)}
                                inputProps={{
                                    min: 0,
                                    step: 0.1,
                                }}
                                helperText={"Example: 10 = 10% Price Increase applied to selected rows."}
                                sx={{marginTop: 1}}
                            />
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleClosePriceChangeModal}>Cancel</Button>
                            <Button variant="contained" onClick={handlePriceIncrease}>Apply Price Increase</Button>
                        </DialogActions>
                    </Dialog>

                </FormProvider>
                )
            }
        />
    )
}

export default SingleCatalog
